import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { CalendarPlus, Loader2, Plus, X } from 'lucide-react';
import { LeadSearchSelect } from './LeadSearchSelect';
import { ConflictWarning } from './ConflictWarning';
import { addMinutesLocal, conflictDetail, errMessage, toLocalDT } from '../../lib/meetings';

const Field = ({ label, children }) => (
  <div className="space-y-1">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    {children}
  </div>
);

// `_rid` is a UI-only stable row key (stripped in cleanPayload) so adding/removing
// an attendee never remounts sibling rows or re-binds inputs to the wrong record.
const rid = () => Math.random().toString(36).slice(2, 10);
const emptyClientAttendee = () => ({ _rid: rid(), name: '', email: '', phone: '', designation: '' });
const withRowIds = (rows) => rows.map((a) => ({ ...a, _rid: a._rid || rid() }));

const defaultStart = () => {
  // IST tomorrow at 11:00 AM, regardless of the device timezone
  const [y, m, d] = new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Kolkata' }).split('-').map(Number);
  const t = new Date(y, m - 1, d + 1); // Date auto-normalises month/year rollover
  const pad = (n) => String(n).padStart(2, '0');
  return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}T11:00`;
};

const buildInitialForm = (meeting, defaultDuration) => {
  if (meeting) {
    return {
      title: meeting.title || '', meeting_type: meeting.meeting_type, mode: meeting.mode,
      location: meeting.location || '', meeting_link: meeting.meeting_link || '',
      start_at: toLocalDT(meeting.start_at), end_at: toLocalDT(meeting.end_at),
      owner_member_id: meeting.owner_member_id || null,
      internal_attendee_ids: meeting.internal_attendee_ids || [],
      client_attendees: meeting.client_attendees?.length ? withRowIds(meeting.client_attendees) : [emptyClientAttendee()],
      agenda: meeting.agenda || '', pre_notes: meeting.pre_notes || '', internal_remarks: meeting.internal_remarks || '',
    };
  }
  const start = defaultStart();
  return {
    title: '', meeting_type: 'Sales Pitch', mode: 'Video Call', location: '', meeting_link: '',
    start_at: start, end_at: addMinutesLocal(start, defaultDuration || 45),
    owner_member_id: null, internal_attendee_ids: [], client_attendees: [emptyClientAttendee()],
    agenda: '', pre_notes: '', internal_remarks: '',
  };
};

const duration = (form) => {
  if (!form.start_at || !form.end_at) return null;
  const mins = Math.round((new Date(form.end_at) - new Date(form.start_at)) / 60000);
  return mins > 0 ? mins : null;
};

const TimeFields = ({ form, set, isEdit }) => (
  <>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Start (IST)">
        <Input type="datetime-local" value={form.start_at} disabled={isEdit}
          onChange={(e) => set('start_at', e.target.value)} data-testid="meeting-start-input" />
      </Field>
      <Field label="End (IST)">
        <Input type="datetime-local" value={form.end_at} disabled={isEdit}
          onChange={(e) => set('end_at', e.target.value)} data-testid="meeting-end-input" />
      </Field>
    </div>
    <div className="text-[11px] text-muted-foreground -mt-1">
      {isEdit ? 'Times are changed via the Reschedule action (keeps full history + notifies attendees).'
        : `Duration: ${duration(form) ? `${duration(form)} min` : '—'}`}
    </div>
  </>
);

const ModeLocationField = ({ form, set, lead }) => {
  if (form.mode === 'In-Person') {
    return (
      <Field label="Venue / Location">
        <Input value={form.location} onChange={(e) => set('location', e.target.value)}
          placeholder="Office address, cafe, client site…" data-testid="meeting-location-input" />
      </Field>
    );
  }
  if (form.mode === 'Video Call') {
    return (
      <Field label="Meeting Link">
        <Input value={form.meeting_link} onChange={(e) => set('meeting_link', e.target.value)}
          placeholder="Paste video call link (Google Meet auto-generation coming with Google connect)"
          data-testid="meeting-link-input" />
      </Field>
    );
  }
  return (
    <div className="rounded-lg border bg-muted/40 px-3 py-2 text-xs text-muted-foreground" data-testid="meeting-phone-note">
      {form.mode} — client number: <span className="font-medium text-foreground">{lead?.phone || 'not on file'}</span>
    </div>
  );
};

/** Live Free/Busy chips for the selected attendees at the chosen slot —
 * powered by the shared team calendar (debounced, edit mode skips itself). */
const FreeBusyStrip = ({ form, members, excludeMeetingId }) => {
  const [status, setStatus] = useState({});
  const ids = form.internal_attendee_ids || [];
  useEffect(() => {
    if (!ids.length || !form.start_at || !form.end_at) { setStatus({}); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get('/meetings/freebusy', {
          params: { member_ids: ids.join(','), start_at: form.start_at, end_at: form.end_at },
        });
        setStatus(data || {});
      } catch { setStatus({}); }
    }, 400);
    return () => clearTimeout(t);
  }, [ids.join(','), form.start_at, form.end_at]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!ids.length || !form.start_at) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="meeting-freebusy-strip">
      {ids.map((id) => {
        const m = members.find((x) => x.id === id);
        const st = status[id];
        const busy = st?.status === 'Busy' && st?.conflict?.id !== excludeMeetingId;
        return (
          <span key={id}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${busy ? 'bg-red-50 text-red-700 border-red-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}
            data-testid={`freebusy-${id}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${busy ? 'bg-red-500' : 'bg-emerald-500'}`} />
            {m?.name || 'Member'}: {busy ? 'Busy at this time' : 'Free'}
          </span>
        );
      })}
    </div>
  );
};

const InternalAttendeesPicker = ({ members, form, set }) => {
  const toggle = (id) => {
    const has = form.internal_attendee_ids.includes(id);
    set('internal_attendee_ids', has ? form.internal_attendee_ids.filter((x) => x !== id) : [...form.internal_attendee_ids, id]);
  };
  const selected = members.filter((m) => form.internal_attendee_ids.includes(m.id));
  return (
    <div className="space-y-2">
      <Field label="Internal Attendees (you are auto-included)">
        <div className="rounded-lg border max-h-32 overflow-y-auto divide-y" data-testid="meeting-attendees-list">
          {members.map((m) => (
            <label key={m.id} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-muted/50">
              <Checkbox checked={form.internal_attendee_ids.includes(m.id)} onCheckedChange={() => toggle(m.id)}
                data-testid={`meeting-attendee-check-${m.id}`} />
              <span className="flex-1 truncate">{m.name}</span>
              <span className="text-[10px] text-muted-foreground">{m.role}</span>
            </label>
          ))}
        </div>
      </Field>
      {selected.length > 0 && (
        <Field label="Meeting Owner">
          <Select value={form.owner_member_id || selected[0]?.id} onValueChange={(v) => set('owner_member_id', v)}>
            <SelectTrigger data-testid="meeting-owner-select"><SelectValue placeholder="Owner" /></SelectTrigger>
            <SelectContent>
              {selected.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      )}
    </div>
  );
};

const ClientAttendeeRow = ({ a, i, update, remove }) => (
  <div className="grid grid-cols-[1fr_1fr_auto] gap-1.5 items-center">
    <Input value={a.name} placeholder="Name" className="h-8 text-xs"
      onChange={(e) => update(i, 'name', e.target.value)} data-testid={`client-attendee-name-${i}`} />
    <Input value={a.email || ''} placeholder="Email" className="h-8 text-xs"
      onChange={(e) => update(i, 'email', e.target.value)} data-testid={`client-attendee-email-${i}`} />
    <div className="flex items-center gap-1.5">
      <Input value={a.phone || ''} placeholder="Phone" className="h-8 text-xs w-28"
        onChange={(e) => update(i, 'phone', e.target.value)} data-testid={`client-attendee-phone-${i}`} />
      <button type="button" onClick={() => remove(i)} className="text-muted-foreground hover:text-destructive"
        aria-label="Remove attendee" data-testid={`client-attendee-remove-${i}`}>
        <X size={14} />
      </button>
    </div>
  </div>
);

const ClientAttendeesEditor = ({ form, set }) => {
  const update = (i, k, v) => {
    const rows = form.client_attendees.map((r, idx) => (idx === i ? { ...r, [k]: v } : r));
    set('client_attendees', rows);
  };
  const remove = (i) => set('client_attendees', form.client_attendees.filter((_, idx) => idx !== i));
  const add = () => set('client_attendees', [...form.client_attendees, emptyClientAttendee()]);
  return (
    <Field label="Client Attendees (primary contact auto-loads from the lead if left blank)">
      <div className="space-y-1.5">
        {form.client_attendees.map((a, i) => (
          <ClientAttendeeRow key={a._rid} a={a} i={i} update={update} remove={remove} />
        ))}
        <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={add} data-testid="client-attendee-add-btn">
          <Plus size={12} className="mr-1" /> Add attendee
        </Button>
      </div>
    </Field>
  );
};

const NotesFields = ({ form, set }) => (
  <>
    <Field label="Agenda">
      <Textarea rows={2} value={form.agenda} onChange={(e) => set('agenda', e.target.value)}
        placeholder="What will be covered…" data-testid="meeting-agenda-input" />
    </Field>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Pre-Meeting Notes">
        <Textarea rows={2} value={form.pre_notes} onChange={(e) => set('pre_notes', e.target.value)} data-testid="meeting-prenotes-input" />
      </Field>
      <Field label="Internal Remarks (never sent to clients)">
        <Textarea rows={2} value={form.internal_remarks} onChange={(e) => set('internal_remarks', e.target.value)} data-testid="meeting-remarks-input" />
      </Field>
    </div>
  </>
);

const cleanPayload = (form, lead) => ({
  ...form,
  lead_id: lead.id,
  title: form.title.trim() || undefined,
  client_attendees: form.client_attendees.filter((a) => (a.name || '').trim()).map(({ _rid, ...a }) => a),
});

/** Form state + save flow for create/edit — kept out of the dialog component. */
const useMeetingFormState = ({ open, meeting, leadProp, onSaved, onClose }) => {
  const isEdit = !!meeting;
  const [meta, setMeta] = useState(null);
  const [lead, setLead] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');

  useEffect(() => {
    if (!open) return;
    api.get('/meetings/meta').then(({ data }) => {
      setMeta(data);
      setForm(buildInitialForm(meeting, data.default_duration_min));
    }).catch(() => toast.error('Failed to load meeting options'));
    setConflict(null);
    setOverrideReason('');
    if (meeting) {
      setLead({ id: meeting.lead_id, lead_name: meeting.lead_name, company_name: meeting.company_name,
        phone: meeting.client_phone, email: meeting.client_email, status: '' });
    } else {
      setLead(leadProp || null);
    }
  }, [open, meeting, leadProp]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (withOverride = false) => {
    if (!lead) { toast.error('Link this meeting to a lead first'); return; }
    if (!form.start_at || !form.end_at) { toast.error('Start and end times are required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...cleanPayload(form, lead),
        override_conflict: withOverride,
        override_reason: withOverride ? overrideReason.trim() : null,
      };
      const { data } = isEdit
        ? await api.put(`/meetings/${meeting.id}`, payload)
        : await api.post('/meetings', payload);
      toast.success(isEdit ? 'Meeting updated' : 'Meeting scheduled');
      onSaved?.(data);
      onClose();
    } catch (e) {
      const c = conflictDetail(e);
      if (c) { setConflict(c); toast.error('Scheduling conflict — see details in the form'); }
      else toast.error(errMessage(e, 'Failed to save meeting'));
    } finally { setSaving(false); }
  };

  return { isEdit, meta, lead, setLead, form, set, saving, conflict, overrideReason, setOverrideReason, submit };
};

const HeaderFields = ({ isEdit, meeting, lead, setLead, form, set, meta }) => (
  <>
    <Field label="Linked Lead / Client *">
      {isEdit ? (
        <div className="rounded-lg border bg-muted/40 px-3 py-2 text-sm font-medium">{meeting.lead_name} {meeting.company_name ? `· ${meeting.company_name}` : ''}</div>
      ) : (
        <LeadSearchSelect value={lead} onSelect={setLead} />
      )}
    </Field>
    <Field label="Meeting Title">
      <Input value={form.title} onChange={(e) => set('title', e.target.value)}
        placeholder={lead ? `Meeting with ${lead.lead_name}` : 'Meeting title'} data-testid="meeting-title-input" />
    </Field>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Meeting Type">
        <Select value={form.meeting_type} onValueChange={(v) => set('meeting_type', v)}>
          <SelectTrigger data-testid="meeting-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {meta.meeting_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Mode">
        <Select value={form.mode} onValueChange={(v) => set('mode', v)}>
          <SelectTrigger data-testid="meeting-mode-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {meta.modes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
    </div>
  </>
);

/** Full meeting create/edit form. Pass `lead` to pre-link; `meeting` for edit mode. */
export const MeetingFormDialog = ({ open, onClose, onSaved, lead: leadProp = null, meeting = null }) => {
  const s = useMeetingFormState({ open, meeting, leadProp, onSaved, onClose });
  const { isEdit, meta, lead, setLead, form, set, saving, conflict, overrideReason, setOverrideReason, submit } = s;
  const members = useMemo(() => meta?.members || [], [meta]);

  if (!open || !form || !meta) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="meeting-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <CalendarPlus size={17} className="text-primary" /> {isEdit ? 'Edit Meeting' : 'Schedule Meeting'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Linked to one CRM lead — outcomes sync back to the Kanban board automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <HeaderFields isEdit={isEdit} meeting={meeting} lead={lead} setLead={setLead}
            form={form} set={set} meta={meta} />
          <ModeLocationField form={form} set={set} lead={lead} />
          <TimeFields form={form} set={set} isEdit={isEdit} />
          <InternalAttendeesPicker members={members} form={form} set={set} />
          <FreeBusyStrip form={form} members={members} excludeMeetingId={meeting?.id} />
          <ClientAttendeesEditor form={form} set={set} />
          <NotesFields form={form} set={set} />

          {conflict && (
            <ConflictWarning conflict={conflict} canOverride={meta.can_override_conflicts}
              overrideReason={overrideReason} setOverrideReason={setOverrideReason}
              onOverride={() => submit(true)} saving={saving} />
          )}

          <div className="flex gap-2 pt-1">
            <Button className="flex-1" onClick={() => submit(false)} disabled={saving} data-testid="meeting-save-btn">
              {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <CalendarPlus size={14} className="mr-1.5" />}
              {isEdit ? 'Save Changes' : 'Schedule Meeting'}
            </Button>
            <Button variant="outline" onClick={onClose} data-testid="meeting-cancel-btn">Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
